import { apiCallJson } from './auth';

export const DEFAULT_MAX_CONCURRENT_EVENTS = 3;

const EFFECTIVE_LIMIT_CACHE_TTL_MS = 10 * 1000;
const effectiveLimitCache = new Map();

export const extractMaximumConcurrentLimit = (settings, fallbackLimit = DEFAULT_MAX_CONCURRENT_EVENTS) => {
  return typeof settings?.maximum_rdv_par_jour === 'number'
    ? settings.maximum_rdv_par_jour
    : fallbackLimit;
};

export const loadMaximumConcurrentLimit = async (options = {}, fallbackLimit = DEFAULT_MAX_CONCURRENT_EVENTS) => {
  const settings = await apiCallJson('/api/calendar-settings', options);
  return extractMaximumConcurrentLimit(settings, fallbackLimit);
};

// 특정날짜별 제한 관리
export const dateLimitAPI = {
  // 목록 조회
  getList: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    return apiCallJson(`/api/calendar-settings/date-limits?${params}`);
  },
  
  // 추가
  create: async (dateLimit) => {
    return apiCallJson('/api/calendar-settings/date-limits', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dateLimit)
    });
  },
  
  // 수정
  update: async (id, dateLimit) => {
    return apiCallJson(`/api/calendar-settings/date-limits/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dateLimit)
    });
  },
  
  // 삭제
  delete: async (id) => {
    return apiCallJson(`/api/calendar-settings/date-limits/${id}`, {
      method: 'DELETE',
    });
  }
};

// 특정날짜의 시간대별 제한 관리
export const dateTimeSlotLimitAPI = {
  // 특정 날짜의 시간대별 제한 목록 조회
  getList: async (date) => {
    return apiCallJson(`/api/calendar-settings/date-limits/${date}/time-slots`);
  },
  
  // 특정 날짜에 시간대별 제한 추가
  create: async (date, timeSlotLimit) => {
    return apiCallJson(`/api/calendar-settings/date-limits/${date}/time-slots`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(timeSlotLimit)
    });
  },
  
  // 특정 날짜의 시간대별 제한 수정
  update: async (date, id, timeSlotLimit) => {
    return apiCallJson(`/api/calendar-settings/date-limits/${date}/time-slots/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(timeSlotLimit)
    });
  },
  
  // 특정 날짜의 시간대별 제한 삭제
  delete: async (date, id) => {
    return apiCallJson(`/api/calendar-settings/date-limits/${date}/time-slots/${id}`, {
      method: 'DELETE',
    });
  }
};

// 금지일정(event-blocks) 관리
export const eventBlockAPI = {
  // 목록 조회
  getList: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);

    return apiCallJson(`/api/admin/event-blocks?${params}`);
  },
  
  // 추가
  create: async (eventBlock) => {
    return apiCallJson('/api/admin/event-blocks', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventBlock)
    });
  },
  
  // 수정
  update: async (id, eventBlock) => {
    return apiCallJson(`/api/admin/event-blocks/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventBlock)
    });
  },
  
  // 삭제
  delete: async (id) => {
    return apiCallJson(`/api/admin/event-blocks/${id}`, {
      method: 'DELETE',
    });
  }
};

// 읽기 전용 금지일정 조회
export const loadPublicEventBlocks = async (startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start', startDate);
  if (endDate) params.append('end', endDate);

  return apiCallJson(`/api/public/event-blocks?${params}`);
};

// 유효한 제한값 조회
export const getEffectiveLimit = async (date, hour) => {
  const normalizedDate = date instanceof Date ? date : new Date(date);
  const params = new URLSearchParams({
    date: normalizedDate.toISOString().split('T')[0],
    hour: hour.toString()
  });

  return apiCallJson(`/api/calendar-settings/effective-limit?${params}`);
};

export const getCachedEffectiveLimit = async (date, hour) => {
  const normalizedDate = date instanceof Date ? date : new Date(date);
  const dateKey = normalizedDate.toISOString().split('T')[0];
  const cacheKey = `${dateKey}-${hour}`;
  const now = Date.now();
  const cached = effectiveLimitCache.get(cacheKey);

  if (cached && now - cached.timestamp < EFFECTIVE_LIMIT_CACHE_TTL_MS) {
    return cached.data;
  }

  const data = await getEffectiveLimit(normalizedDate, hour);

  if (data?.success) {
    effectiveLimitCache.set(cacheKey, {
      timestamp: now,
      data
    });
  }

  return data;
};

export const clearEffectiveLimitCache = () => {
  effectiveLimitCache.clear();
};



// 전역 동시일정제한 설정 관리
export const globalLimitAPI = {
  // 현재 설정 조회
  getCurrent: async (options = {}) => {
    return apiCallJson('/api/calendar-settings', options);
  },
  
  // 설정 업데이트
  update: async (maximum_rdv_par_jour) => {
    return apiCallJson('/api/calendar-settings', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ maximum_rdv_par_jour })
    });
  }
};
